/**
 * Public Campus Summary Component
 * 
 * Public-facing campus summary page that displays:
 * - Population Trends
 * - Area School Comparison
 * - Projected Enrollment
 * 
 * No authentication required. Accessed via /campuses/:slug
 */

import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { TrendingUp, InfoOutlined } from '@mui/icons-material';
import NavigationBar from './NavigationBar';
import SiteFooter from './SiteFooter';
import PublicCampusHeader from './PublicCampusHeader';
import usePublicCampusData from '../../hooks/usePublicCampusData';
import { 
  calculateGradeFilteredPopulation
} from '../../utils/schoolUtils';

// Import analysis components (we'll add isPublic prop to these)
import PopulationAnalysis from '../school_summary/display_components/PopulationAnalysis';
import ProjectionsAnalysis from '../school_summary/display_components/ProjectionsAnalysis';

// Import visualization components
import GeoXCommunityTrends from '../geographic_explorer/community/GeoXCommunityTrends';
import SimplePopulationTable from '../school_summary/display_components/SimplePopulationTable';
import ProjectXProjectionsChart from '../projections_explorer/dashboard/ProjectXProjectionsChart';
import UnifiedSchoolMap from '../shared_components/unifiedSchoolMap';
import PublicMapPopup from './PublicMapPopup';

// Import skeleton loaders
import {
  PopulationTrendsSkeleton,
  ComparisonSkeleton,
  ProjectionsSkeleton
} from '../school_summary/loading_components/SkeletonLoaders';

const PublicCampusSummary = () => {
  const { schoolSlug } = useParams();
  const navigate = useNavigate();
  
  const {
    campusInfo,
    populationData,
    comparisonData,
    nearbySchools,
    projectionsData,
    selectedDriveTime,
    selectedTab,
    loading,
    error,
    handleDriveTimeChange,
    handleTabChange
  } = usePublicCampusData(schoolSlug);

  // Set page title when campus info loads
  useEffect(() => {
    if (campusInfo) {
      document.title = `${campusInfo.school.name} - Enrollment Insights | Enrollment Data Cooperative`;
    }
  }, [campusInfo]);

  // Show loading state for initial campus info
  if (loading.campusInfo) {
    return (
      <>
        <NavigationBar />
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '80vh' 
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Loading campus information...
            </Typography>
          </Box>
        </Box>
        <SiteFooter />
      </>
    );
  }

  // Show error state
  if (error) {
    return (
      <>
        <NavigationBar />
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
          >
            <Typography variant="h6" gutterBottom>
              {error.status === 404 
                ? 'Campus Not Found' 
                : 'Unable to Load Campus Information'}
            </Typography>
            <Typography variant="body2">
              {error.message || 'This campus may not be available or the URL may be incorrect.'}
            </Typography>
          </Alert>
          <Button 
            variant="contained" 
            onClick={() => navigate('/')}
            sx={{ mt: 2 }}
          >
            Return to Home
          </Button>
        </Container>
        <SiteFooter />
      </>
    );
  }

  // Show main content
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <NavigationBar />
      
      {/* Header with logo, tabs, and controls */}
      <PublicCampusHeader
        campusInfo={campusInfo}
        selectedDriveTime={selectedDriveTime}
        selectedTab={selectedTab}
        onDriveTimeChange={handleDriveTimeChange}
        onTabChange={handleTabChange}
      />

      {/* Main Content */}
      <Box sx={{ flex: 1, bgcolor: 'grey.100', py: 4 }}>
        <Container maxWidth="lg">
          {/* Tab Content */}
          <Box sx={{ mb: 4 }}>
            {/* Population Trends Tab */}
            {selectedTab === 0 && (
              <Box>
                {loading.population ? (
                  <PopulationTrendsSkeleton />
                ) : populationData ? (
                  <>
                    {/* Calculate population totals once */}
                    {(() => {
                      // Format population data for the utility function
                      const formattedEsriData = {
                        drive_times: {
                          [selectedDriveTime]: {
                            ages: {
                              '4_17': populationData.ages['4_17']
                            }
                          }
                        }
                      };
                      
                      // Get school's grades and convert to format expected by utility functions
                      const rawGrades = campusInfo?.school?.grades || [];
                      const schoolGrades = rawGrades.map(grade => {
                        if (grade === 'K' || grade === 'Kindergarten') return 'Kindergarten';
                        if (grade === 'PK' || grade === 'Pre-Kindergarten') return 'Pre-Kindergarten';
                        return `Grade ${grade}`;
                      });
                      
                      // Calculate filtered population totals based on school's grades
                      const populationTotals = calculateGradeFilteredPopulation({
                        esriData: formattedEsriData,
                        selectedDriveTime: selectedDriveTime,
                        selectedGrades: schoolGrades,
                        includePreK: false
                      });
                      
                      const currentPop = populationTotals.current || 0;
                      const pastPop = populationTotals.past || 0;
                      const futurePop = populationTotals.future || 0;
                      const currentVsPastChange = pastPop > 0 ? ((currentPop - pastPop) / pastPop) * 100 : 0;
                      const currentVsFutureChange = currentPop > 0 ? ((futurePop - currentPop) / currentPop) * 100 : 0;
                      
                      return (
                        <>
                          <PopulationAnalysis
                            selectedSchool={campusInfo?.school}
                            populationTotals={{
                              current: currentPop,
                              past: pastPop,
                              future: futurePop
                            }}
                            currentVsPastChange={currentVsPastChange}
                            selectedDriveTime={selectedDriveTime}
                            selectedGrades={schoolGrades}
                            user={null}
                            isPublic={true}
                          />
                          
                          {/* Key Metrics Cards */}
                          <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
                            <Grid item xs={12} sm={4}>
                              <Card elevation={2}>
                                <CardContent>
                                  <Typography variant="overline" color="text.secondary" gutterBottom>
                                    Current Population
                                  </Typography>
                                  <Typography variant="h4" color="primary">
                                    {Math.round(currentPop).toLocaleString()}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    school-age children
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Card elevation={2}>
                                <CardContent>
                                  <Typography variant="overline" color="text.secondary" gutterBottom>
                                    Change Since 2020
                                  </Typography>
                                  <Typography 
                                    variant="h4" 
                                    color={Math.abs(currentVsPastChange) < 5 ? 'text.primary' : currentVsPastChange > 0 ? 'success.main' : 'error.main'}
                                  >
                                    {currentVsPastChange > 0 ? '+' : ''}{(currentVsPastChange ?? 0).toFixed(0)}%
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {Math.abs(Math.round(currentPop - pastPop)).toLocaleString()} children
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Card elevation={2}>
                                <CardContent>
                                  <Typography variant="overline" color="text.secondary" gutterBottom>
                                    Projected 2030
                                  </Typography>
                                  <Typography 
                                    variant="h4" 
                                    color={Math.abs(currentVsFutureChange) < 5 ? 'text.primary' : currentVsFutureChange > 0 ? 'success.main' : 'error.main'}
                                  >
                                    {Math.round(futurePop).toLocaleString()}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {currentVsFutureChange > 0 ? '+' : ''}{(currentVsFutureChange ?? 0).toFixed(0)}% change
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                          </Grid>
                          
                          {/* Population Trends Chart */}
                          <Box sx={{ mt: 2 }}>
                            <GeoXCommunityTrends
                              populationTotals={{
                                current: currentPop,
                                past: pastPop,
                                future: futurePop
                              }}
                              totalChange={pastPop > 0 ? ((futurePop - pastPop) / pastPop) * 100 : 0}
                              selectedDriveTime={selectedDriveTime}
                              animationEnabled={true}
                            />
                          </Box>
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <Alert severity="info">
                    Population data not available for this campus.
                  </Alert>
                )}
              </Box>
            )}

            {/* Area School Comparison Tab */}
            {selectedTab === 1 && (
              <Box>
                {loading.comparison ? (
                  <ComparisonSkeleton />
                ) : comparisonData && comparisonData.nearby_schools && comparisonData.nearby_schools.length > 0 ? (
                  <>
                    {/* Summary Card */}
                    <Paper 
                      elevation={0}
                      sx={{
                        mb: 2,
                        py: 2,
                        px: 3,
                        backgroundColor: 'rgba(103, 58, 183, 0.08)',
                        color: 'text.primary',
                        borderRadius: 2,
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 6,
                          backgroundColor: 'primary.main',
                          borderTopLeftRadius: 8,
                          borderBottomLeftRadius: 8
                        }
                      }}
                    >
                      <Typography variant="h8" sx={{ fontWeight: 200 }}>
                        {comparisonData.main_school?.enrollment_change_pct !== null && 
                         comparisonData.main_school?.rank !== null && 
                         comparisonData.main_school?.earliest_year && 
                         comparisonData.main_school?.latest_year ? (
                          <>
                            <strong>{campusInfo?.school?.name}</strong>'s enrollment change of{' '}
                            <strong>{comparisonData.main_school.enrollment_change_pct > 0 ? '+' : ''}{(comparisonData.main_school.enrollment_change_pct ?? 0).toFixed(0)}%</strong>{' '}
                            ranks <strong>{comparisonData.main_school.rank}{['st', 'nd', 'rd'][((comparisonData.main_school.rank + 90) % 100 - 10) % 10 - 1] || 'th'}</strong> out of{' '}
                            <strong>{comparisonData.main_school.total_ranked}</strong> nearby schools between{' '}
                            <strong>{comparisonData.main_school.earliest_year}</strong> and <strong>{comparisonData.main_school.latest_year}</strong>.
                          </>
                        ) : (
                          <>
                            There are <strong>{comparisonData.nearby_schools.length}</strong> schools within a {selectedDriveTime}-minute drive of <strong>{campusInfo?.school?.name}</strong>.
                          </>
                        )}
                      </Typography>
                    </Paper>

                    {/* Key Metrics Cards */}
                    {comparisonData.main_school && (
                      <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
                        <Grid item xs={12} sm={4}>
                          <Card elevation={2}>
                            <CardContent>
                              <Typography variant="overline" color="text.secondary" gutterBottom>
                                Current Enrollment
                              </Typography>
                              <Typography variant="h4" color="primary">
                                {Math.round(comparisonData.main_school.current_enrollment || 0).toLocaleString()}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {comparisonData.main_school.latest_year} school year
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Card elevation={2}>
                            <CardContent>
                              <Typography variant="overline" color="text.secondary" gutterBottom>
                                Change Since {comparisonData.main_school.earliest_year}
                              </Typography>
                              <Typography 
                                variant="h4" 
                                color={
                                  Math.abs(comparisonData.main_school.enrollment_change_pct ?? 0) < 5 
                                    ? 'text.primary' 
                                    : (comparisonData.main_school.enrollment_change_pct ?? 0) > 0 
                                      ? 'success.main' 
                                      : 'error.main'
                                }
                              >
                                {(comparisonData.main_school.enrollment_change_pct ?? 0) > 0 ? '+' : ''}{(comparisonData.main_school.enrollment_change_pct ?? 0).toFixed(0)}%
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {Math.abs(Math.round((comparisonData.main_school.current_enrollment || 0) - (comparisonData.main_school.previous_enrollment || 0))).toLocaleString()} students
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Card elevation={2}>
                            <CardContent>
                              <Typography variant="overline" color="text.secondary" gutterBottom>
                                Rank Among Area Schools
                              </Typography>
                              <Typography 
                                variant="h4" 
                                color="primary"
                              >
                                {comparisonData.main_school.rank}{['st', 'nd', 'rd'][((comparisonData.main_school.rank + 90) % 100 - 10) % 10 - 1] || 'th'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                out of {comparisonData.main_school.total_ranked} schools
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    )}

                    {/* Note about overlapping grades */}
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: 'italic', color: 'text.secondary', mt: 2, mb: 1 }}
                    >
                      All enrollment data shown for overlapping grades only
                    </Typography>

                    {/* Map and Table Side-by-Side */}
                    {nearbySchools && nearbySchools.schools && nearbySchools.schools.length > 0 && nearbySchools.center && (() => {
                      // Helper to convert governance to charter/private fields
                      const getSchoolType = (governance) => ({
                        charter: governance === 'charter' ? 'Yes' : 'No',
                        is_private: governance === 'private'
                      });
                      
                      // Parse polygon data if it exists
                      let polygonData = null;
                      if (nearbySchools.polygon) {
                        try {
                          polygonData = typeof nearbySchools.polygon === 'string' 
                            ? JSON.parse(nearbySchools.polygon) 
                            : nearbySchools.polygon;
                        } catch (e) {
                          console.error('Error parsing polygon data:', e);
                        }
                      }
                      
                      // Format esriData to match the expected structure for UnifiedSchoolMap
                      // The map expects: { drive_times: { 10: { drive_time_polygon: <data> } } }
                      const esriData = polygonData ? {
                        drive_times: {
                          [selectedDriveTime]: {
                            drive_time_polygon: polygonData
                          }
                        }
                      } : null;
                      
                      
                      // Build enrollment data from comparison data for color coding and popup
                      const schoolsEnrollmentData = comparisonData?.nearby_schools?.map(school => {
                        // Create enrollment_by_grade structure with all grades
                        // Put all enrollment in 'Kindergarten' as a simple way to make totals work
                        // This is a workaround since we don't have grade-by-grade data from the API
                        const allGrades = {};
                        for (let i = 1; i <= 12; i++) {
                          allGrades[`Grade ${i}`] = 0;
                        }
                        
                        // Access with snake_case as returned from backend
                        const currentEnroll = school.current_enrollment || 0;
                        const previousEnroll = school.previous_enrollment || 0;
                        
                        const enrollmentData = {
                          uuid: school.uuid,
                          enrollment_by_grade: {
                            current: { 
                              Kindergarten: currentEnroll,
                              ...allGrades
                            },
                            '2019-2020': { 
                              Kindergarten: previousEnroll,
                              ...allGrades
                            }
                          },
                          // Add data for popup
                          current_enrollment: currentEnroll,
                          previous_enrollment: previousEnroll,
                          enrollment_change_pct: school.enrollment_change_pct
                        };
                        
                        return enrollmentData;
                      }) || [];
                      
                      // Add main school's enrollment data if available
                      if (comparisonData?.main_school && campusInfo?.school?.uuid) {
                        const mainSchoolEnrollment = {
                          uuid: campusInfo.school.uuid,
                          enrollment_by_grade: {
                            current: { Kindergarten: comparisonData.main_school.current_enrollment || 0 },
                            '2019-2020': { Kindergarten: comparisonData.main_school.previous_enrollment || 0 }
                          },
                          current_enrollment: comparisonData.main_school.current_enrollment,
                          previous_enrollment: comparisonData.main_school.previous_enrollment,
                          enrollment_change_pct: comparisonData.main_school.enrollment_change_pct
                        };
                        schoolsEnrollmentData.push(mainSchoolEnrollment);
                      }
                      
                      return (
                        <Card elevation={2} sx={{ mt: 2 }}>
                          <CardContent>
                            <Grid container spacing={2}>
                              {/* Map - 2/3 width */}
                              <Grid item xs={12} md={8}>
                                <Box sx={{ 
                                  height: 500, 
                                  width: '100%',
                                  position: 'relative',
                                  overflow: 'hidden',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider'
                                }}>
                                  <UnifiedSchoolMap
                                    mode="geographic"
                                    selectedSchool={{
                                      uuid: campusInfo?.school?.uuid || '',
                                      name: campusInfo?.school?.name || '',
                                      latitude: nearbySchools.center.latitude,
                                      longitude: nearbySchools.center.longitude,
                                      charter: 'No',
                                      is_private: false,
                                      drive_time: selectedDriveTime,
                                      governance: campusInfo?.school?.governance || 'district',
                                      grade_range: campusInfo?.school?.grades ? campusInfo.school.grades.join(', ') : null
                                    }}
                                    filteredSchools={nearbySchools.schools.map(school => {
                                      const schoolType = getSchoolType(school.governance);
                                      return {
                                        ...school,
                                        drive_time: selectedDriveTime,
                                        charter: schoolType.charter,
                                        is_private: schoolType.is_private
                                      };
                                    })}
                                    esriData={esriData}
                                    visibleDriveTimes={[selectedDriveTime]}
                                    schoolsEnrollmentData={schoolsEnrollmentData}
                                    governanceFilter=""
                                    selectedGrades={[]}
                                    schoolTypeFilter=""
                                    PopupComponent={PublicMapPopup}
                                    onSchoolSelect={() => {}}
                                    focusedSchool={null}
                                    initialZoom={12}
                                  />
                                </Box>
                              </Grid>

                              {/* Table - 1/3 width */}
                              <Grid item xs={12} md={4}>
                                <Box sx={{ height: 500, overflow: 'auto' }}>
                                  <SimplePopulationTable 
                                    schools={comparisonData.nearby_schools.map(school => ({
                                      uuid: school.uuid,
                                      name: school.name,
                                      governance: school.governance,
                                      currentEnrollment: school.current_enrollment,
                                      previousEnrollment: school.previous_enrollment,
                                      enrollmentChange: school.enrollment_change_pct
                                    }))}
                                    selectedSchoolTrends={{}}
                                    focusedSchool={null}
                                    selectedSchool={campusInfo?.school}
                                    onSchoolSelect={() => {}}
                                    isPublic={true}
                                  />
                                </Box>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      );
                    })()}
                  </>
                ) : (
                  <Alert severity="info">
                    School comparison data not available for this campus.
                  </Alert>
                )}
              </Box>
            )}

            {/* Projected Enrollment Tab */}
            {selectedTab === 2 && (
              <Box>
                {loading.projections ? (
                  <ProjectionsSkeleton />
                ) : projectionsData ? (
                  <>
                    {(() => {
                      // Calculate enrollment metrics
                      const actualYears = Object.keys(projectionsData.actual_enrollment || {}).sort();
                      const latestActualYear = actualYears[actualYears.length - 1];
                      const earliestActualYear = actualYears[0];
                      
                      const latestActualTotal = latestActualYear ? 
                        Object.values(projectionsData.actual_enrollment[latestActualYear]).reduce((sum, val) => sum + (val || 0), 0) : 
                        0;
                      
                      const earliestActualTotal = earliestActualYear ? 
                        Object.values(projectionsData.actual_enrollment[earliestActualYear]).reduce((sum, val) => sum + (val || 0), 0) : 
                        0;
                      
                      const projectionYears = Object.keys(projectionsData.projections?.median || {}).sort();
                      const lastProjectionYear = projectionYears[projectionYears.length - 1];
                      
                      const lastProjectionTotal = lastProjectionYear ?
                        Object.values(projectionsData.projections.median[lastProjectionYear]).reduce((sum, val) => sum + (val || 0), 0) :
                        0;
                      
                      const historicalChange = earliestActualTotal > 0 ?
                        ((latestActualTotal - earliestActualTotal) / earliestActualTotal) * 100 : 0;
                      
                      const projectedChange = latestActualTotal > 0 ?
                        ((lastProjectionTotal - latestActualTotal) / latestActualTotal) * 100 : 0;
                      
                      return (
                        <>
                          <ProjectionsAnalysis
                            selectedSchool={campusInfo?.school}
                            enrollmentData={projectionsData}
                            user={null}
                            isPublic={true}
                          />
                          
                          {/* Key Metrics Cards */}
                          <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
                            <Grid item xs={12} sm={4}>
                              <Card elevation={2}>
                                <CardContent>
                                  <Typography variant="overline" color="text.secondary" gutterBottom>
                                    Current Enrollment
                                  </Typography>
                                  <Typography variant="h4" color="primary">
                                    {Math.round(latestActualTotal).toLocaleString()}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {latestActualYear} school year
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Card elevation={2}>
                                <CardContent>
                                  <Typography variant="overline" color="text.secondary" gutterBottom>
                                    Change Since {earliestActualYear}
                                  </Typography>
                                  <Typography 
                                    variant="h4" 
                                    color={Math.abs(historicalChange ?? 0) < 5 ? 'text.primary' : (historicalChange ?? 0) > 0 ? 'success.main' : 'error.main'}
                                  >
                                    {(historicalChange ?? 0) > 0 ? '+' : ''}{(historicalChange ?? 0).toFixed(0)}%
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {Math.abs(Math.round(latestActualTotal - earliestActualTotal)).toLocaleString()} students
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Card elevation={2}>
                                <CardContent>
                                  <Typography variant="overline" color="text.secondary" gutterBottom>
                                    Projected {lastProjectionYear}
                                  </Typography>
                                  <Typography 
                                    variant="h4" 
                                    color={Math.abs(projectedChange ?? 0) < 5 ? 'text.primary' : (projectedChange ?? 0) > 0 ? 'success.main' : 'error.main'}
                                  >
                                    {Math.round(lastProjectionTotal).toLocaleString()}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {(projectedChange ?? 0) > 0 ? '+' : ''}{(projectedChange ?? 0).toFixed(0)}% change
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                          </Grid>
                          
                          {/* Projections Chart */}
                          <Card elevation={2} sx={{ mt: 2 }}>
                            <CardContent>
                              {/* Header */}
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <TrendingUp sx={{ color: 'primary.main', mr: 1 }} />
                                <Typography variant="h6" sx={{ 
                                  flexGrow: 1, 
                                  fontWeight: 600
                                }}>
                                  Projected Enrollment
                                </Typography>
                                <Tooltip title="Projected changes in school enrollment">
                                  <IconButton size="small">
                                    <InfoOutlined />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              
                              {/* Chart */}
                              <Box sx={{ height: 400 }}>
                                <ProjectXProjectionsChart
                                  school={campusInfo?.school}
                                  enrollmentData={projectionsData}
                                  selectedGrades={campusInfo?.school?.grades || []}
                                  timeframe="5-year"
                                  view="school-level"
                                  targets={{}}
                                  selectedScenarios={['low', 'median', 'high']}
                                />
                              </Box>
                            </CardContent>
                          </Card>
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <Alert severity="info">
                    Enrollment projections not available for this campus.
                  </Alert>
                )}
              </Box>
            )}
          </Box>

          {/* Call to Action */}
          <Paper 
            elevation={2}
            sx={{ 
              p: 4, 
              textAlign: 'center',
              bgcolor: 'primary.main',
              color: 'white'
            }}
          >
            <LockOpenIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Want to Explore More?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
              {campusInfo?.team?.funded_by_csgf && (
                <>
                  Thanks to the Charter School Growth Fund, {campusInfo.team.name} has access to the complete version of School Explorer for each of your schools.{' '}
                </>
              )}
              Click Access Your Account to explore in-depth data for each campus, including nearby population, demographics, competition, and enrollment projections.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/reset-password')}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                fontWeight: 600,
                px: 4,
                '&:hover': {
                  bgcolor: 'grey.100'
                }
              }}
            >
              Access Your Account
            </Button>
          </Paper>
        </Container>
      </Box>

      <SiteFooter />
    </Box>
  );
};

export default PublicCampusSummary;

